import { lazy, Suspense, useEffect, useState } from 'react';
import { UnsavedChangesDialog } from '../ui/UnsavedChangesDialog';
import { RecoveryHost } from '../ui/RecoveryHost';
import { SaveConflictDialogs } from '../ui/SaveConflictDialogs';
import { DXF_EXPORT_DIALOG_EVENT, type DxfExportRequest } from '../../services/cad/dxf/exportDialogBridge';
import { PLOT_DIALOG_EVENT, type PlotDialogRequest } from '../../services/plot/plotDialogBridge';
import { PAGE_SETUP_DIALOG_EVENT, type PageSetupDialogRequest } from '../../services/plot/plotDialogBridge';

// Event-driven dialogs fetch their modules on first open and stay mounted for
// the session afterwards. The unsaved-changes guard and the recovery host stay
// eager on purpose: both must observe store/bridge events from boot.
const NewProjectDialog = lazy(() =>
    import('../ui/NewProjectDialog').then(m => ({ default: m.NewProjectDialog })));
const SaveTemplateDialog = lazy(() =>
    import('../ui/SaveTemplateDialog').then(m => ({ default: m.SaveTemplateDialog })));
const FileBrowserDialog = lazy(() =>
    import('../ui/FileBrowserDialog').then(m => ({ default: m.FileBrowserDialog })));
const DxfExportDialog = lazy(() =>
    import('../ui/DxfExportDialog').then(m => ({ default: m.DxfExportDialog })));
const PlotDialog = lazy(() =>
    import('../cad/PlotDialog').then(m => ({ default: m.PlotDialog })));
const PageSetupDialog = lazy(() =>
    import('../cad/PageSetupDialog').then(m => ({ default: m.PageSetupDialog })));
const AliasEditDialog = lazy(() =>
    import('../ui/AliasEditDialog').then(m => ({ default: m.AliasEditDialog })));

/**
 * Project Dialogs Host
 *
 * Mounts the document-level dialogs once for the whole application and
 * opens them via window events, so any command, tool or menu can trigger
 * them without prop drilling:
 * - 'yap:open-new-project-dialog'  → New Project (template gallery)
 * - 'yap:open-save-template-dialog' → Save Project as Template
 * - 'yap:open-file-browser'        → in-app file browser (fallback FS UI)
 * - 'yap:open-dxf-export-dialog'   → DXF export (version target + save)
 * - 'yap:open-plot-dialog'         → PLOT / PUBLISH (vector PDF, Faz 2)
 * - 'yap:open-pagesetup-dialog'    → PAGESETUP (per-layout page setup, Faz 2)
 * The unsaved-changes guard is store-driven (useUnsavedChangesStore), so it
 * simply mounts here too.
 */
export function ProjectDialogsHost() {
    const [newProjectOpen, setNewProjectOpen] = useState(false);
    const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
    const [browserMode, setBrowserMode] = useState<'open' | 'save' | null>(null);
    const [dxfRequest, setDxfRequest] = useState<DxfExportRequest | null>(null);
    const [dxfOpen, setDxfOpen] = useState(false);
    const [plotRequest, setPlotRequest] = useState<PlotDialogRequest | null>(null);
    const [plotOpen, setPlotOpen] = useState(false);
    const [pageSetupRequest, setPageSetupRequest] = useState<PageSetupDialogRequest | null>(null);
    const [pageSetupOpen, setPageSetupOpen] = useState(false);
    const [aliasEditOpen, setAliasEditOpen] = useState(false);
    // Mount-once gates: once a dialog has been opened its module stays
    // mounted, so internal state survives open/close cycles as before.
    const [newProjectMounted, setNewProjectMounted] = useState(false);
    const [saveTemplateMounted, setSaveTemplateMounted] = useState(false);
    const [browserMounted, setBrowserMounted] = useState(false);
    const [dxfMounted, setDxfMounted] = useState(false);
    const [plotMounted, setPlotMounted] = useState(false);
    const [pageSetupMounted, setPageSetupMounted] = useState(false);

    useEffect(() => {
        const handleNewProject = () => {
            setNewProjectMounted(true);
            setNewProjectOpen(true);
        };
        const handleSaveTemplate = () => {
            setSaveTemplateMounted(true);
            setSaveTemplateOpen(true);
        };
        const handleFileBrowser = (e: Event) => {
            setBrowserMounted(true);
            setBrowserMode((e as CustomEvent).detail?.mode === 'save' ? 'save' : 'open');
        };
        const handleDxfExport = (e: Event) => {
            setDxfMounted(true);
            setDxfRequest((e as CustomEvent).detail ?? { mode: 'drawing' });
            setDxfOpen(true);
        };
        const handlePlot = (e: Event) => {
            setPlotMounted(true);
            setPlotRequest((e as CustomEvent).detail ?? { mode: 'single' });
            setPlotOpen(true);
        };
        const handlePageSetup = (e: Event) => {
            setPageSetupMounted(true);
            setPageSetupRequest((e as CustomEvent).detail ?? { layoutId: null });
            setPageSetupOpen(true);
        };
        const handleAliasEdit = () => setAliasEditOpen(true);

        window.addEventListener('yap:open-new-project-dialog', handleNewProject);
        window.addEventListener('yap:open-save-template-dialog', handleSaveTemplate);
        window.addEventListener('yap:open-file-browser', handleFileBrowser);
        window.addEventListener(DXF_EXPORT_DIALOG_EVENT, handleDxfExport);
        window.addEventListener(PLOT_DIALOG_EVENT, handlePlot);
        window.addEventListener(PAGE_SETUP_DIALOG_EVENT, handlePageSetup);
        window.addEventListener('yap:open-alias-edit-dialog', handleAliasEdit);
        return () => {
            window.removeEventListener('yap:open-new-project-dialog', handleNewProject);
            window.removeEventListener('yap:open-save-template-dialog', handleSaveTemplate);
            window.removeEventListener('yap:open-file-browser', handleFileBrowser);
            window.removeEventListener(DXF_EXPORT_DIALOG_EVENT, handleDxfExport);
            window.removeEventListener(PLOT_DIALOG_EVENT, handlePlot);
            window.removeEventListener(PAGE_SETUP_DIALOG_EVENT, handlePageSetup);
            window.removeEventListener('yap:open-alias-edit-dialog', handleAliasEdit);
        };
    }, []);

    return (
        <>
            {newProjectMounted && (
                <Suspense fallback={null}>
                    <NewProjectDialog isOpen={newProjectOpen} onClose={() => setNewProjectOpen(false)} />
                </Suspense>
            )}
            {saveTemplateMounted && (
                <Suspense fallback={null}>
                    <SaveTemplateDialog isOpen={saveTemplateOpen} onClose={() => setSaveTemplateOpen(false)} />
                </Suspense>
            )}
            {browserMounted && (
                <Suspense fallback={null}>
                    <FileBrowserDialog
                        isOpen={browserMode !== null}
                        mode={browserMode ?? 'open'}
                        onClose={() => setBrowserMode(null)}
                    />
                </Suspense>
            )}
            {dxfMounted && (
                <Suspense fallback={null}>
                    <DxfExportDialog
                        isOpen={dxfOpen}
                        request={dxfRequest}
                        onClose={() => setDxfOpen(false)}
                    />
                </Suspense>
            )}
            {plotMounted && (
                <Suspense fallback={null}>
                    <PlotDialog
                        isOpen={plotOpen}
                        request={plotRequest}
                        onClose={() => setPlotOpen(false)}
                    />
                </Suspense>
            )}
            {pageSetupMounted && (
                <Suspense fallback={null}>
                    <PageSetupDialog
                        isOpen={pageSetupOpen}
                        request={pageSetupRequest}
                        onClose={() => setPageSetupOpen(false)}
                    />
                </Suspense>
            )}
            <UnsavedChangesDialog />
            <SaveConflictDialogs />
            <RecoveryHost />
            {aliasEditOpen && (
                <Suspense fallback={null}>
                    <AliasEditDialog isOpen={aliasEditOpen} onClose={() => setAliasEditOpen(false)} />
                </Suspense>
            )}
        </>
    );
}
